import { useState, useEffect, useContext, createContext, useCallback, useRef } from 'react';

const MOBILE_BREAKPOINT = 1000;

export const isios = /iP(ad|od|hone)/i.test(window.navigator.userAgent) || ('ontouchstart' in window && navigator.platform == "MacIntel" && typeof navigator.standalone !== "undefined");
export const isandroid = /(android)/i.test(navigator.userAgent);

// Context for managing global resize listener
const ResizeContext = createContext();

export function ResizeProvider({ children }) {
  const listenersRef = useRef(new Set());
  const dimensionsRef = useRef({ width: 0, height: 0 });

  useEffect(() => {
    if (isios) {
      document.documentElement.classList.add('isios');
    } else {
      document.documentElement.classList.remove('isios');
    }
  }, []);

  // Initialize dimensions and body class
  useEffect(() => {
    if (typeof window !== 'undefined') {
      dimensionsRef.current = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      
      // Set initial body class
      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      if (isMobile) {
        document.documentElement.classList.add('is_mobile');
      } else {
        document.documentElement.classList.remove('is_mobile');
      }
    }
  }, []);

  const handleResize = useCallback(() => {
    if (typeof window !== 'undefined') {
      const newDimensions = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      
      dimensionsRef.current = newDimensions;
      
      // Update body class for mobile state
      const isMobile = newDimensions.width <= MOBILE_BREAKPOINT;
      if (isMobile) {
        document.documentElement.classList.add('is_mobile');
      } else {
        document.documentElement.classList.remove('is_mobile');
      }
      
      // Notify all registered listeners
      listenersRef.current.forEach(listener => {
        listener(newDimensions.width, newDimensions.height);
      });
    }
  }, []);

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    
    // Call immediately with current dimensions
    if (typeof window !== 'undefined') {
      listener(dimensionsRef.current.width, dimensionsRef.current.height);
    }
    
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      // Call once to set initial dimensions
      handleResize();
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [handleResize]);

  const contextValue = {
    subscribe,
    getCurrentDimensions: () => dimensionsRef.current
  };

  return (
    <ResizeContext.Provider value={contextValue}>
      {children}
    </ResizeContext.Provider>
  );
}

function useResizeContext() {
  const context = useContext(ResizeContext);
  if (!context) {
    throw new Error('useResizeContext must be used within a ResizeProvider');
  }
  return context;
}

export function useIsMobile() {
  return useBreakpoint((width) => width <= MOBILE_BREAKPOINT);
}

export function useBreakpoint(evaluatorFn) {
  const { subscribe } = useResizeContext();
  const [breakpointResult, setBreakpointResult] = useState(() => {
    if (typeof window !== 'undefined') {
      return evaluatorFn(window.innerWidth, window.innerHeight);
    }
    return false;
  });

  useEffect(() => {
    const unsubscribe = subscribe((width, height) => {
      const newResult = evaluatorFn(width, height);
      // Only update state if the result actually changed to avoid unnecessary re-renders
      setBreakpointResult(currentResult => {
        return newResult !== currentResult ? newResult : currentResult;
      });
    });

    return unsubscribe;
  }, [subscribe, evaluatorFn]);

  return breakpointResult;
}

export function useResizeEffect(callback) {
  const { subscribe } = useResizeContext();

  useEffect(() => {
    const unsubscribe = subscribe((width, height) => {
      callback(width, height);
    });

    return unsubscribe;
  }, [subscribe, callback]);
}
